// The one realtime hub (/hubs/realtime) over a bare WebSocket: the SignalR JSON hub protocol, which is all the
// SignalR client did for this cabinet. WebSockets only and no negotiate - a socket opened after a separate negotiate
// can reach another API task, which refuses it - and the token in the query string, as SignalR passes it to a socket.
//
// Protocol: a handshake {"protocol":"json","version":1}, then JSON messages each ended by 0x1E. Type 1 is an
// invocation (target + arguments), 6 a ping, 7 a close.

import { getAccessToken } from './auth.ts';
import { log } from './logs.ts';

const SEPARATOR = '\u001e';
const RECONNECT_DELAYS_MS = [0, 2000, 10000, 30000];
const KEEP_ALIVE_MS = 15000;

/** A hub method's handler: the invocation's arguments, as the server sent them. */
export type HubHandler = (...args: unknown[]) => void;

interface HubMessage {
    type?: number;
    target?: string;
    arguments?: unknown[];
    error?: string;
}

/** hubUrl is the hub's https URL. */
export function connectHub(hubUrl: string, options: { handlers: Record<string, HubHandler>; onReconnected?: () => void }): { stop: () => void } {
    let socket: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;
    let everConnected = false;
    let keepAlive: ReturnType<typeof setInterval> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    function schedule(): void {
        if (stopped) return;

        // Like WithAutomaticReconnect, and on past its four tries: a cabinet left open overnight should still hear.
        const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)];

        attempt++;
        retryTimer = setTimeout(open, delay);
    }

    function open(): void {
        if (stopped) return;

        const url = new URL(hubUrl);

        url.protocol = url.protocol === 'http:' ? 'ws:' : 'wss:';

        const token = getAccessToken();

        if (token) url.searchParams.set('access_token', token);

        let opened: WebSocket;

        try {
            opened = new WebSocket(url.toString());
        } catch (error) {
            log.warn(`Live dashboard updates unavailable: ${error instanceof Error ? error.message : String(error)}`, undefined, 'Lify.Influencer.Pages.Dashboard');
            schedule();

            return;
        }

        socket = opened;

        let buffer = '';
        let handshaken = false;

        opened.onopen = () => {
            opened.send(JSON.stringify({ protocol: 'json', version: 1 }) + SEPARATOR);
        };

        opened.onmessage = event => {
            buffer += typeof event.data === 'string' ? event.data : '';

            let index: number;

            while ((index = buffer.indexOf(SEPARATOR)) >= 0) {
                const frame = buffer.slice(0, index);

                buffer = buffer.slice(index + 1);

                let message: HubMessage;

                try {
                    message = JSON.parse(frame) as HubMessage;
                } catch {
                    continue;
                }

                if (!handshaken) {
                    handshaken = true;

                    if (message.error) {
                        log.warn(`Hub handshake refused: ${message.error}`, undefined, 'Lify.Influencer.Pages.Dashboard');
                        opened.close();

                        return;
                    }

                    attempt = 0;
                    clearInterval(keepAlive);
                    keepAlive = setInterval(() => {
                        if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 6 }) + SEPARATOR);
                    }, KEEP_ALIVE_MS);

                    // A reconnect means time passed unheard; whatever was missed is caught up now.
                    if (everConnected) options.onReconnected?.();
                    everConnected = true;

                    continue;
                }

                const handler = message.type === 1 && message.target ? options.handlers[message.target] : undefined;

                if (handler) {
                    try {
                        handler(...(message.arguments || []));
                    } catch (error) {
                        log.error(`Hub handler ${message.target} failed`, error);
                    }
                } else if (message.type === 7) {
                    opened.close();
                }
            }
        };

        opened.onclose = () => {
            clearInterval(keepAlive);
            socket = null;
            schedule();
        };
    }

    open();

    return {
        stop() {
            stopped = true;
            clearTimeout(retryTimer);
            clearInterval(keepAlive);

            if (socket) {
                socket.onclose = null;
                socket.close();
                socket = null;
            }
        }
    };
}
